import { createFileRoute, Link } from "@tanstack/react-router";
import {
	AlertCircle,
	ArrowLeft,
	Calendar,
	CheckCircle,
	CreditCard,
	Edit,
	Image,
	Plus,
	Search,
	Trash2,
	User,
	Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute(
	"/_auth/app/_app/help/user-guide/enrollment-process",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();

	return (
		<div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6 sm:space-y-8">
			{/* Mobile Back Button */}
			<Link
				to="/app/help/user-guide"
				className="md:hidden flex items-center gap-2 text-primary"
			>
				<ArrowLeft className="w-4 h-4" />
				{t("common.back")}
			</Link>

			{/* Header */}
			<div className="mb-6 sm:mb-8">
				<div className="flex items-center space-x-3 mb-4">
					<div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-50 rounded-lg flex items-center justify-center shrink-0">
						<Users className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
					</div>
					<div className="min-w-0">
						<h1 className="text-xl sm:text-3xl font-bold text-base-content">
							{t("help.enrollmentProcess")}
						</h1>
						<p className="text-sm sm:text-lg text-base-content/70">
							{t("help.enrollmentProcessDescription")}
						</p>
					</div>
				</div>
			</div>

			{/* Overview */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Users className="h-5 w-5" />
						<span>{t("help.enrollmentProcessOverview")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.enrollmentProcessOverviewDescription")}
					</p>
					<div className="bg-info/10 border border-info/20 rounded-lg p-4">
						<div className="flex items-start space-x-3">
							<AlertCircle className="h-5 w-5 text-info mt-0.5" />
							<div>
								<h4 className="font-medium text-info">
									{t("help.enrollmentProcessKeyFeatures")}
								</h4>
								<ul className="text-sm text-info/80 mt-2 space-y-1">
									<li>• {t("help.enrollmentProcessFeature1")}</li>
									<li>• {t("help.enrollmentProcessFeature2")}</li>
									<li>• {t("help.enrollmentProcessFeature3")}</li>
									<li>• {t("help.enrollmentProcessFeature4")}</li>
									<li>• {t("help.enrollmentProcessFeature5")}</li>
								</ul>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Accessing Enrollment Management */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Users className="h-5 w-5" />
						<span>{t("help.enrollmentProcessAccessing")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.enrollmentProcessAccessingDescription")}
					</p>
					<ol className="list-decimal list-inside space-y-2 text-base-content/80">
						<li>{t("help.enrollmentProcessAccessingStep1")}</li>
						<li>{t("help.enrollmentProcessAccessingStep2")}</li>
						<li>{t("help.enrollmentProcessAccessingStep3")}</li>
						<li>{t("help.enrollmentProcessAccessingStep4")}</li>
					</ol>
					<div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
						<div className="flex items-start space-x-3">
							<AlertCircle className="h-5 w-5 text-warning mt-0.5" />
							<div>
								<h4 className="font-medium text-warning">
									{t("help.enrollmentProcessPermissionRequirements")}
								</h4>
								<p className="text-sm text-warning/80 mt-1">
									{t("help.enrollmentProcessPermissionDescription")}
								</p>
							</div>
						</div>
					</div>
					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.enrollmentProcessImagePlaceholder1")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Enrollment List Management */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Search className="h-5 w-5" />
						<span>{t("help.enrollmentListManagement")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.enrollmentListManagementDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentListSearchFiltering")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.enrollmentListRealTimeSearch")}</li>
								<li>• {t("help.enrollmentListDebouncedInput")}</li>
								<li>• {t("help.enrollmentListAutoPagination")}</li>
								<li>• {t("help.enrollmentListLoadingIndicators")}</li>
							</ul>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentListTableInfo")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.enrollmentListCustomerInfo")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.enrollmentListCustomerInfoDescription")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.enrollmentListPlanDetails")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.enrollmentListPlanDetailsDescription")}
										</p>
									</div>
								</div>
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.enrollmentListScheduleInfo")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.enrollmentListScheduleInfoDescription")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.enrollmentListEnrollmentStatus")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.enrollmentListEnrollmentStatusDescription")}
										</p>
									</div>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentListAvailableActions")}
							</h4>
							<div className="space-y-2">
								<div>
									<h5 className="font-medium text-sm text-base-content flex items-center">
										<Edit className="h-4 w-4 mr-2" />
										{t("help.enrollmentListEditAction")}
									</h5>
									<p className="text-sm text-base-content/70">
										{t("help.enrollmentListEditDescription")}
									</p>
								</div>
								<div>
									<h5 className="font-medium text-sm text-base-content flex items-center">
										<Trash2 className="h-4 w-4 mr-2" />
										{t("help.enrollmentListDeleteAction")}
									</h5>
									<p className="text-sm text-base-content/70">
										{t("help.enrollmentListDeleteDescription")}
									</p>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentListPaginationPerformance")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.enrollmentListConfigurablePageSize")}</li>
								<li>• {t("help.enrollmentListPaginationControls")}</li>
								<li>• {t("help.enrollmentListTotalCountDisplay")}</li>
								<li>• {t("help.enrollmentListOptimizedLoading")}</li>
							</ul>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.enrollmentProcessImagePlaceholder2")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Creating New Enrollments */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Plus className="h-5 w-5" />
						<span>{t("help.enrollmentCreating")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.enrollmentCreatingDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentStep1")}
							</h4>
							<div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
								<h5 className="font-medium text-primary mb-2 flex items-center">
									<User className="h-4 w-4 mr-2" />
									{t("help.enrollmentStep1Title")}
								</h5>
								<ul className="text-sm text-primary/80 space-y-1">
									<li>• {t("help.enrollmentStep1Feature1")}</li>
									<li>• {t("help.enrollmentStep1Feature2")}</li>
									<li>• {t("help.enrollmentStep1Feature3")}</li>
									<li>• {t("help.enrollmentStep1Feature4")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentStep2")}
							</h4>
							<div className="bg-success/10 border border-success/20 rounded-lg p-4">
								<h5 className="font-medium text-success mb-2 flex items-center">
									<CreditCard className="h-4 w-4 mr-2" />
									{t("help.enrollmentStep2Title")}
								</h5>
								<ul className="text-sm text-success/80 space-y-1">
									<li>• {t("help.enrollmentStep2Feature1")}</li>
									<li>• {t("help.enrollmentStep2Feature2")}</li>
									<li>• {t("help.enrollmentStep2Feature3")}</li>
									<li>• {t("help.enrollmentStep2Feature4")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentStep3")}
							</h4>
							<div className="bg-info/10 border border-info/20 rounded-lg p-4">
								<h5 className="font-medium text-info mb-2 flex items-center">
									<Calendar className="h-4 w-4 mr-2" />
									{t("help.enrollmentStep3Title")}
								</h5>
								<ul className="text-sm text-info/80 space-y-1">
									<li>• {t("help.enrollmentStep3Feature1")}</li>
									<li>• {t("help.enrollmentStep3Feature2")}</li>
									<li>• {t("help.enrollmentStep3Feature3")}</li>
									<li>• {t("help.enrollmentStep3Feature4")}</li>
									<li>• {t("help.enrollmentStep3Feature5")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentStep4")}
							</h4>
							<div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
								<h5 className="font-medium text-warning mb-2 flex items-center">
									<Calendar className="h-4 w-4 mr-2" />
									{t("help.enrollmentStep4Title")}
								</h5>
								<ul className="text-sm text-warning/80 space-y-1">
									<li>• {t("help.enrollmentStep4Feature1")}</li>
									<li>• {t("help.enrollmentStep4Feature2")}</li>
									<li>• {t("help.enrollmentStep4Feature3")}</li>
									<li>• {t("help.enrollmentStep4Feature4")}</li>
									<li>• {t("help.enrollmentStep4Feature5")}</li>
								</ul>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.enrollmentProcessImagePlaceholder3")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Validation and Conflict Detection */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<AlertCircle className="h-5 w-5" />
						<span>{t("help.enrollmentValidation")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.enrollmentValidationDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentScheduleOverlap")}
							</h4>
							<div className="bg-error/10 border border-error/20 rounded-lg p-4">
								<h5 className="font-medium text-error mb-2">
									{t("help.enrollmentTimeConflictPrevention")}
								</h5>
								<ul className="text-sm text-error/80 space-y-1">
									<li>• {t("help.enrollmentValidationFeature1")}</li>
									<li>• {t("help.enrollmentValidationFeature2")}</li>
									<li>• {t("help.enrollmentValidationFeature3")}</li>
									<li>• {t("help.enrollmentValidationFeature4")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentWeeklyLimits")}
							</h4>
							<div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
								<h5 className="font-medium text-warning mb-2">
									{t("help.enrollmentPlanLimitEnforcement")}
								</h5>
								<ul className="text-sm text-warning/80 space-y-1">
									<li>• {t("help.enrollmentLimitFeature1")}</li>
									<li>• {t("help.enrollmentLimitFeature2")}</li>
									<li>• {t("help.enrollmentLimitFeature3")}</li>
									<li>• {t("help.enrollmentLimitFeature4")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentPlanCompatibility")}
							</h4>
							<div className="bg-info/10 border border-info/20 rounded-lg p-4">
								<h5 className="font-medium text-info mb-2">
									{t("help.enrollmentEstablishmentFiltering")}
								</h5>
								<ul className="text-sm text-info/80 space-y-1">
									<li>• {t("help.enrollmentCompatibilityFeature1")}</li>
									<li>• {t("help.enrollmentCompatibilityFeature2")}</li>
									<li>• {t("help.enrollmentCompatibilityFeature3")}</li>
									<li>• {t("help.enrollmentCompatibilityFeature4")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentFormValidation")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.enrollmentFormRule1")}</li>
								<li>• {t("help.enrollmentFormRule2")}</li>
								<li>• {t("help.enrollmentFormRule3")}</li>
								<li>• {t("help.enrollmentFormRule4")}</li>
							</ul>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.enrollmentProcessImagePlaceholder4")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Confirmation and Submission */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<CheckCircle className="h-5 w-5" />
						<span>{t("help.enrollmentConfirmation")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.enrollmentConfirmationDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentConfirmationModal")}
							</h4>
							<p className="text-sm text-base-content/70 mb-3">
								{t("help.enrollmentConfirmationModalDescription")}
							</p>
							<div className="bg-success/10 border border-success/20 rounded-lg p-4">
								<h5 className="font-medium text-success mb-2">
									{t("help.enrollmentConfirmationDetails")}
								</h5>
								<ul className="text-sm text-success/80 space-y-1">
									<li>• {t("help.enrollmentConfirmationFeature1")}</li>
									<li>• {t("help.enrollmentConfirmationFeature2")}</li>
									<li>• {t("help.enrollmentConfirmationFeature3")}</li>
									<li>• {t("help.enrollmentConfirmationFeature4")}</li>
									<li>• {t("help.enrollmentConfirmationFeature5")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentBulkCreation")}
							</h4>
							<p className="text-sm text-base-content/70 mb-3">
								{t("help.enrollmentBulkCreationDescription")}
							</p>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.enrollmentBulkFeature1")}</li>
								<li>• {t("help.enrollmentBulkFeature2")}</li>
								<li>• {t("help.enrollmentBulkFeature3")}</li>
								<li>• {t("help.enrollmentBulkFeature4")}</li>
							</ul>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentSuccessError")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.enrollmentSuccessFlow")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.enrollmentSuccessFlowDescription")}
										</p>
									</div>
								</div>
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.enrollmentErrorHandling")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.enrollmentErrorHandlingDescription")}
										</p>
									</div>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentSubmitButton")}
							</h4>
							<div className="bg-info/10 border border-info/20 rounded-lg p-4">
								<h5 className="font-medium text-info mb-2">
									{t("help.enrollmentDynamicButtonBehavior")}
								</h5>
								<ul className="text-sm text-info/80 space-y-1">
									<li>• {t("help.enrollmentButtonFeature1")}</li>
									<li>• {t("help.enrollmentButtonFeature2")}</li>
									<li>• {t("help.enrollmentButtonFeature3")}</li>
									<li>• {t("help.enrollmentButtonFeature4")}</li>
								</ul>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.enrollmentProcessImagePlaceholder5")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Editing Enrollments */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Edit className="h-5 w-5" />
						<span>{t("help.enrollmentEditing")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.enrollmentEditingDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentEditModeDifferences")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-3">
									<div className="bg-success/10 border border-success/20 rounded-lg p-3">
										<h5 className="font-medium text-success mb-2">
											{t("help.enrollmentEditableFields")}
										</h5>
										<ul className="text-sm text-success/80 space-y-1">
											<li>• {t("help.enrollmentEditableField1")}</li>
											<li>• {t("help.enrollmentEditableField2")}</li>
											<li>• {t("help.enrollmentEditableField3")}</li>
											<li>• {t("help.enrollmentEditableField4")}</li>
											<li>• {t("help.enrollmentEditableField5")}</li>
										</ul>
									</div>
								</div>
								<div className="space-y-3">
									<div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
										<h5 className="font-medium text-warning mb-2">
											{t("help.enrollmentEditLimitations")}
										</h5>
										<ul className="text-sm text-warning/80 space-y-1">
											<li>• {t("help.enrollmentEditLimitation1")}</li>
											<li>• {t("help.enrollmentEditLimitation2")}</li>
											<li>• {t("help.enrollmentEditLimitation3")}</li>
											<li>• {t("help.enrollmentEditLimitation4")}</li>
										</ul>
									</div>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentStatusManagement")}
							</h4>
							<div className="space-y-2">
								<div className="bg-base-100 border border-base-200 rounded-lg p-3">
									<h5 className="font-medium text-sm text-base-content mb-2">
										{t("help.enrollmentAvailableStatusOptions")}
									</h5>
									<ul className="text-sm text-base-content/70 space-y-1">
										<li>• {t("help.enrollmentStatusActive")}</li>
										<li>• {t("help.enrollmentStatusPaused")}</li>
										<li>• {t("help.enrollmentStatusCanceled")}</li>
										<li>• {t("help.enrollmentStatusCompleted")}</li>
									</ul>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.enrollmentDataPreservation")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.enrollmentDataFeature1")}</li>
								<li>• {t("help.enrollmentDataFeature2")}</li>
								<li>• {t("help.enrollmentDataFeature3")}</li>
								<li>• {t("help.enrollmentDataFeature4")}</li>
							</ul>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.enrollmentProcessImagePlaceholder6")}</em>
						</p>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
